/**
 * Declarative confirm-modal helper — Phase 18 of FRONTEND_AUDIT_AND_PLAN.md.
 *
 * Replaces the ``onclick="showConfirmModal({title, message, ..., onConfirm: () => form.submit()})"``
 * inline pattern that was scattered across 11 buttons in 6 office
 * templates. Each occurrence used the *exact* same shape:
 *   - static i18n strings for title/message/confirmText
 *   - an ``onConfirm`` callback that just submits a form
 *
 * The new contract is purely declarative — any element with
 * ``data-confirm-action`` participates:
 *
 *   <button type="button"
 *           data-confirm-action="submit-form"
 *           data-confirm-target="my-form-id"          {# optional — falls back to closest('form') #}
 *           data-confirm-title="Delete?"
 *           data-confirm-message="This cannot be undone."
 *           data-confirm-text="Delete"
 *           data-confirm-cancel-text="Cancel"          {# optional #}
 *           data-confirm-type="danger">                {# optional, default 'warning' #}
 *     ...
 *   </button>
 *
 * Why a single delegated listener on ``document.body``:
 *   - Survives every HTMX swap (boosted nav, partial reload, drawer
 *     content injection) without re-binding — the listener is bound
 *     ONCE per SPA lifetime via the ``_confirmActionBound`` flag.
 *   - No per-page module + no init walker required, so templates can
 *     adopt the pattern without coordinating with a JS module.
 *
 * Why ``requestSubmit()`` over ``submit()``:
 *   - Triggers the ``submit`` event so HTMX-bound forms (``hx-post`` &
 *     friends) actually fire their AJAX request instead of falling
 *     back to a native form POST that bypasses HTMX entirely (this is
 *     exactly what the legacy CODA delete handler in
 *     ``templates/office/billing/coda/_list.html`` was doing — it
 *     called ``requestSubmit()`` explicitly for that reason).
 *   - ``requestSubmit()`` is available in every browser this app
 *     targets (Safari 16+, Chrome 76+, Firefox 75+); ``submit()`` is
 *     kept as a defensive fallback.
 */

const CONFIRM_TRIGGER_SELECTOR = '[data-confirm-action]';

/**
 * Resolve the target form for a ``submit-form`` action.
 *
 * Precedence:
 *   1. ``data-confirm-target="form-id"`` → ``getElementById('form-id')``
 *   2. ``trigger.closest('form')``
 */
function resolveSubmitTarget(trigger) {
    const explicitId = trigger.dataset.confirmTarget;
    if (explicitId) {
        return document.getElementById(explicitId);
    }
    return trigger.closest('form');
}

function submitForm(form) {
    if (!form) return false;
    if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
    } else {
        form.submit();
    }
    return true;
}

function dispatchConfirmedAction(trigger) {
    const action = trigger.dataset.confirmAction;
    switch (action) {
        case 'submit-form':
            return submitForm(resolveSubmitTarget(trigger));
        // Future actions (navigate, click-target, htmx-trigger, ...) plug
        // in here without changing the template-side contract.
        default:
            console.warn('[confirm-action] Unknown action:', action, trigger);
            return false;
    }
}

function buildConfirmConfig(trigger) {
    const ds = trigger.dataset;
    const cfg = {
        title: ds.confirmTitle || '',
        message: ds.confirmMessage || '',
        confirmText: ds.confirmText || 'Confirm',
        type: ds.confirmType || 'warning',
        onConfirm: () => dispatchConfirmedAction(trigger),
    };
    if (ds.confirmCancelText) cfg.cancelText = ds.confirmCancelText;
    return cfg;
}

function handleConfirmClick(event) {
    const trigger = event.target.closest(CONFIRM_TRIGGER_SELECTOR);
    if (!trigger) return;

    // Stop the event before showConfirmModal even runs — the trigger is
    // typically a <button type="button"> but defensive prevention also
    // covers the (legacy) cases where it's a <button type="submit"> or
    // an <a href="#">.
    event.preventDefault();
    event.stopPropagation();

    if (typeof window.showConfirmModal !== 'function') {
        // Hard-fallback: the global modal helper is missing (very early
        // page load, or it failed to bundle). Better to act on a native
        // confirm() than to silently swallow the click.
        if (window.confirm(trigger.dataset.confirmMessage || 'Are you sure?')) {
            dispatchConfirmedAction(trigger);
        }
        return;
    }

    window.showConfirmModal(buildConfirmConfig(trigger));
}

/**
 * Bind the delegated handler. Idempotent — safe to call from
 * ``DOMContentLoaded`` and re-call paths; only the first call wires
 * up the listener.
 *
 * IMPORTANT — bound in the **capture** phase, not the default bubble.
 *
 * Several office tables (notably ``customers/_tab_worksites.html``,
 * ``cleaners/availability.html``) wrap their action ``<td>`` in
 * ``onclick="event.stopPropagation()"`` to keep clicks on action icons
 * from also triggering the row's own click handler (row navigates to
 * detail / Flowbite accordion toggles / etc.). A bubble-phase listener
 * on ``document.body`` would NEVER fire on those buttons because the
 * ``<td>`` swallows the event on its way up.
 *
 * Capture-phase bypasses this entirely: ``document → body → ... → td → button``,
 * so we receive the click BEFORE any intermediate ancestor can stop it.
 * We then call ``stopPropagation()`` ourselves to make the modal-open
 * authoritative — the row's own handler would have been wrong here
 * anyway.
 *
 * Regression that motivated the switch: Phase 18 worksite "Archive"
 * action on the customer detail page (2026-05-11) — the button was
 * inert because of the action-cell ``stopPropagation`` guard above it.
 */
export function initConfirmAction() {
    if (window._confirmActionBound) return;
    window._confirmActionBound = true;
    document.body.addEventListener('click', handleConfirmClick, true);
}
